import BigNumber from 'bignumber.js'
import React, { useCallback, useMemo, useState } from 'react'
import Button from '../../../components/Button'
import Label from '../../../components/Label'
import Modal, { ModalProps } from '../../../components/Modal'
import ModalActions from '../../../components/ModalActions'
import ModalTitle from '../../../components/ModalTitle'
import TokenInput from '../../../components/TokenInput'
import useAllowedStakeIncrement from '../../../hooks/useAllowedStakeIncrement'
import { getFullDisplayBalance } from '../../../utils/formatBalance'

interface WithdrawModalProps extends ModalProps {
  max: BigNumber
  onConfirm: (amount: string) => void
  tokenName?: string
}

const WithdrawModal: React.FC<WithdrawModalProps> = ({
  onConfirm,
  onDismiss,
  max,
  tokenName = '',
}) => {
  const [val, setVal] = useState('')
  const [pendingTx, setPendingTx] = useState(false)

  const fullBalance = useMemo(() => {
    return getFullDisplayBalance(max)
  }, [max])

  const handleChange = useCallback(
    (e: React.FormEvent<HTMLInputElement>) => {
      setVal(e.currentTarget.value)
    },
    [setVal],
  )

  const handleSelectMax = useCallback(() => {
    setVal(fullBalance)
  }, [fullBalance, setVal])

  const increment = useAllowedStakeIncrement(0);

  const handleIncreaseValue = useCallback(() => {
    const allowedIncrement = increment.div(10 ** 18).toNumber()
    if (parseInt(val) >= parseInt(fullBalance)) {
      return;
    }

    if (!val) {
      setVal(allowedIncrement.toString())
    } else {
      setVal((parseInt(val) + allowedIncrement).toString())
    }
  }, [fullBalance, setVal, val, increment, fullBalance])

  const handleDecreaseValue = useCallback(() => {
    const allowedIncrement = increment.div(10 ** 18).toNumber()
    if (parseInt(val) > 0) {
      setVal((parseInt(val) - allowedIncrement).toString())
    }
  }, [fullBalance, setVal, val, increment])

  return (
    <Modal>
      <ModalTitle text={`Withdraw ${tokenName}`} />
      <TokenInput
        onSelectMax={handleSelectMax}
        onChange={handleChange}
        onIncreaseValue={handleIncreaseValue}
        onDecreaseValue={handleDecreaseValue}
        value={val}
        max={fullBalance}
        symbol={tokenName}
      />

      {
        val.length > 0 && parseInt(val) % increment.div(10 ** 18).toNumber() != 0 ?
          <Label text={'Withdraw must be an increment of ' + increment.div(10 ** 18).toString()} color='#FFF'/>
          : <></>
      }

      <ModalActions>
        <Button text="Cancel" variant="secondary" onClick={onDismiss} />
        <Button
          disabled={parseInt(val) % increment.div(10 ** 18).toNumber() != 0 || pendingTx}
          text={pendingTx ? 'Pending Confirmation' : 'Confirm'}
          onClick={async () => {
            setPendingTx(true)
            await onConfirm(val)
            setPendingTx(false)
            onDismiss()
          }}
        />
      </ModalActions>
    </Modal>
  )
}

export default WithdrawModal
